import { zodResolver } from '@hookform/resolvers/zod';
import { getListing } from 'graphql/listings/queries';
import { createPartyAssociation, GQLCreatePartyAssociation } from 'graphql/parties/mutations';
import useSearchParty from 'hooks/useSearchParty';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { NewPartyType, NewUserType } from 'types/new-user';
import { CACHE_30_DAYS, LISTING_CODE_ALLOWED_ORDER_ASSOCIATIONS } from 'utils/constants';
import { z } from 'zod';
import validations from 'utils/validations';

type Params = {
  partyId: string;
  onSuccess: (data: GQLCreatePartyAssociation) => void;
  onError: (error: unknown) => void;
  onClose: () => void;
};

export const useModalCreateAssociationHooks = ({
  partyId,
  onSuccess,
  onClose,
  onError,
}: Params) => {
  const { t } = useTranslation();

  const schema = z.object({
    _id: validations.requiredString(t('validations:required')),
    name: validations.requiredString(t('validations:required')),
    kind: validations.requiredString(t('validations:required')),
  }).passthrough();

  const [searchText, setSearchText] = useState<string>('');

  const { parties } = useSearchParty(searchText);
  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm<NewPartyType & { selected: NewUserType | null; type: 'member of' | 'subordinate' }>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { _id: '', name: '', kind: '', selected: null, type: 'member of' },
  });

  const { data: listingResponse } = useQuery(
    ['getListing', LISTING_CODE_ALLOWED_ORDER_ASSOCIATIONS],
    {
      queryFn: () => getListing({ code: LISTING_CODE_ALLOWED_ORDER_ASSOCIATIONS }),
      refetchOnWindowFocus: false,
      staleTime: CACHE_30_DAYS,
    }
  );

  const { mutate: createAssociationMutation, isLoading: isSaving } = useMutation(createPartyAssociation, {
    onSuccess: (data) => {
      onSuccess(data);
      reset();
      onClose();
    },
    onError,
  });

  const createAssociation = handleSubmit((data) => {
    const { type } = data;

    createAssociationMutation({
      id: type === 'member of' ? data._id : partyId,
      partyId: type === 'member of' ? partyId : data._id,
      kind: data.kind,
    });
  });

  return {
    schema,
    reset,
    watch,
    control,
    setValue,
    searchText,
    setSearchText,
    parties,
    errors,
    isValid,
    createAssociation,
    isSaving,
    t,
    allowedAssociations: listingResponse?.getListing.entries || [],
    getValues
  };
};
